/**
 * Responsive Manager Utility
 * Handles responsive breakpoints, touch detection, and mobile optimizations
 */

interface BreakpointConfig {
  name: string;
  minWidth: number;
  maxWidth?: number;
}

interface ResponsiveState {
  currentBreakpoint: string;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isTouchDevice: boolean;
  orientation: 'portrait' | 'landscape';
  viewportWidth: number;
  viewportHeight: number;
}

type ResponsiveCallback = (state: ResponsiveState) => void;

export class ResponsiveManager {
  private static instance: ResponsiveManager;
  private breakpoints: BreakpointConfig[] = [
    { name: 'mobile-small', minWidth: 0, maxWidth: 479 },
    { name: 'mobile', minWidth: 480, maxWidth: 767 },
    { name: 'tablet', minWidth: 768, maxWidth: 1023 },
    { name: 'desktop', minWidth: 1024, maxWidth: 1439 },
    { name: 'desktop-large', minWidth: 1440 }
  ];
  
  private callbacks: Set<ResponsiveCallback> = new Set();
  private currentState: ResponsiveState;
  private resizeObserver?: ResizeObserver;
  private mediaQueryLists: Map<string, MediaQueryList> = new Map();

  private constructor() {
    this.currentState = this.calculateState();
    this.init();
  }

  public static getInstance(): ResponsiveManager {
    if (!ResponsiveManager.instance) {
      ResponsiveManager.instance = new ResponsiveManager();
    }
    return ResponsiveManager.instance;
  }

  private init(): void {
    this.setupMediaQueries();
    this.setupResizeObserver();
    this.setupOrientationListener();
    this.detectTouchCapability();
    this.optimizeForMobile();
  }

  private setupMediaQueries(): void {
    this.breakpoints.forEach(breakpoint => {
      let query = `(min-width: ${breakpoint.minWidth}px)`;
      if (breakpoint.maxWidth) {
        query += ` and (max-width: ${breakpoint.maxWidth}px)`;
      }

      const mediaQuery = window.matchMedia(query);
      this.mediaQueryLists.set(breakpoint.name, mediaQuery);
      
      mediaQuery.addEventListener('change', () => {
        this.updateState();
      });
    });

    // Additional useful media queries
    const touchQuery = window.matchMedia('(hover: none) and (pointer: coarse)');
    touchQuery.addEventListener('change', () => {
      this.updateState();
    });
  }

  private setupResizeObserver(): void {
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => {
        this.updateState();
      });
      this.resizeObserver.observe(document.documentElement);
    } else {
      // Fallback for older browsers
      const resizeHandler = this.debounce(() => {
        this.updateState();
      }, 100);
      (window as any).addEventListener('resize', resizeHandler);
    }
  }

  private setupOrientationListener(): void {
    // Modern approach
    if ('orientation' in screen && screen.orientation) {
      const orientation = screen.orientation as any;
      if ('addEventListener' in orientation) {
        orientation.addEventListener('change', () => {
          setTimeout(() => this.updateState(), 100); // Small delay for orientation change
        });
      }
    } else {
      // Fallback
      window.addEventListener('orientationchange', () => {
        setTimeout(() => this.updateState(), 100);
      });
    }
  }

  private detectTouchCapability(): void {
    // Multiple methods to detect touch capability
    const hasTouch = (
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      (navigator as any).msMaxTouchPoints > 0
    );

    // Add CSS class for touch devices
    if (hasTouch) {
      document.documentElement.classList.add('touch-device');
    } else {
      document.documentElement.classList.add('no-touch');
    }
  }

  private optimizeForMobile(): void {
    // Prevent zoom on input focus for iOS
    if (this.isIOS()) {
      const viewport = document.querySelector('meta[name="viewport"]') as HTMLMetaElement;
      if (viewport) {
        const content = viewport.content;
        if (!content.includes('user-scalable=no')) {
          viewport.content = content + ', user-scalable=no';
        }
      }
    }

    // Add mobile-specific optimizations
    if (this.currentState.isMobile) {
      document.documentElement.classList.add('mobile-optimized');
      
      // Optimize touch scrolling
      (document.body.style as any).webkitOverflowScrolling = 'touch';
      
      // Prevent pull-to-refresh on mobile
      document.body.style.overscrollBehavior = 'none';
    }
  }

  private calculateState(): ResponsiveState {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    let currentBreakpoint = 'desktop';
    for (const breakpoint of this.breakpoints) {
      if (viewportWidth >= breakpoint.minWidth && 
          (!breakpoint.maxWidth || viewportWidth <= breakpoint.maxWidth)) {
        currentBreakpoint = breakpoint.name;
        break;
      }
    }

    const isMobile = currentBreakpoint.includes('mobile');
    const isTablet = currentBreakpoint === 'tablet';
    const isDesktop = currentBreakpoint.includes('desktop');
    
    const isTouchDevice = this.detectTouch();
    const orientation = viewportWidth > viewportHeight ? 'landscape' : 'portrait';

    return {
      currentBreakpoint,
      isMobile,
      isTablet,
      isDesktop,
      isTouchDevice,
      orientation,
      viewportWidth,
      viewportHeight
    };
  }

  private detectTouch(): boolean {
    const touchQuery = window.matchMedia('(hover: none) and (pointer: coarse)');
    return touchQuery.matches || 'ontouchstart' in window;
  }

  private updateState(): void {
    const newState = this.calculateState();
    const hasChanged = JSON.stringify(newState) !== JSON.stringify(this.currentState);
    
    if (hasChanged) {
      this.currentState = newState;
      this.updateBodyClasses();
      this.notifyCallbacks();
    }
  }

  private updateBodyClasses(): void {
    const { currentBreakpoint, isMobile, isTablet, isDesktop, isTouchDevice, orientation } = this.currentState;
    
    // Remove old classes
    document.documentElement.className = document.documentElement.className
      .replace(/\b(mobile-small|mobile|tablet|desktop|desktop-large)\b/g, '')
      .replace(/\b(is-mobile|is-tablet|is-desktop)\b/g, '')
      .replace(/\b(touch-device|no-touch)\b/g, '')
      .replace(/\b(portrait|landscape)\b/g, '')
      .trim();

    // Add current classes
    const classes = [
      currentBreakpoint,
      isMobile ? 'is-mobile' : '',
      isTablet ? 'is-tablet' : '',
      isDesktop ? 'is-desktop' : '',
      isTouchDevice ? 'touch-device' : 'no-touch',
      orientation
    ].filter(Boolean);

    document.documentElement.classList.add(...classes);
  }

  private notifyCallbacks(): void {
    this.callbacks.forEach(callback => {
      try {
        callback(this.currentState);
      } catch (error) {
        console.error('Error in responsive callback:', error);
      }
    });
  }

  private debounce(func: Function, wait: number): Function {
    let timeout: number;
    return function executedFunction(...args: any[]) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = window.setTimeout(later, wait);
    };
  }

  private isIOS(): boolean {
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  }

  // Public API
  public getState(): ResponsiveState {
    return { ...this.currentState };
  }

  public isMobile(): boolean {
    return this.currentState.isMobile;
  }

  public isTablet(): boolean {
    return this.currentState.isTablet;
  }

  public isDesktop(): boolean {
    return this.currentState.isDesktop;
  }

  public isTouchDevice(): boolean {
    return this.currentState.isTouchDevice;
  }

  public getBreakpoint(): string {
    return this.currentState.currentBreakpoint;
  }

  public getOrientation(): 'portrait' | 'landscape' {
    return this.currentState.orientation;
  }

  public getViewportSize(): { width: number; height: number } {
    return {
      width: this.currentState.viewportWidth,
      height: this.currentState.viewportHeight
    };
  }

  public subscribe(callback: ResponsiveCallback): () => void {
    this.callbacks.add(callback);
    
    // Return unsubscribe function
    return () => {
      this.callbacks.delete(callback);
    };
  }

  public matchesBreakpoint(breakpointName: string): boolean {
    const mediaQuery = this.mediaQueryLists.get(breakpointName);
    return mediaQuery ? mediaQuery.matches : false;
  }

  public matchesQuery(query: string): boolean {
    return window.matchMedia(query).matches;
  }

  // Utility methods for common responsive patterns
  public onMobile(callback: () => void): void {
    if (this.isMobile()) {
      callback();
    }
  }

  public onTablet(callback: () => void): void {
    if (this.isTablet()) {
      callback();
    }
  }

  public onDesktop(callback: () => void): void {
    if (this.isDesktop()) {
      callback();
    }
  }

  public onTouch(callback: () => void): void {
    if (this.isTouchDevice()) {
      callback();
    }
  }

  public onBreakpointChange(breakpoint: string, callback: () => void): () => void {
    return this.subscribe((state) => {
      if (state.currentBreakpoint === breakpoint) {
        callback();
      }
    });
  }

  // CSS custom properties management
  public updateCSSCustomProperties(): void {
    const root = document.documentElement;
    const { viewportWidth, viewportHeight } = this.currentState;
    
    root.style.setProperty('--viewport-width', `${viewportWidth}px`);
    root.style.setProperty('--viewport-height', `${viewportHeight}px`);
    root.style.setProperty('--is-mobile', this.isMobile() ? '1' : '0');
    root.style.setProperty('--is-tablet', this.isTablet() ? '1' : '0');
    root.style.setProperty('--is-desktop', this.isDesktop() ? '1' : '0');
    root.style.setProperty('--is-touch', this.isTouchDevice() ? '1' : '0');
  }

  public destroy(): void {
    this.callbacks.clear();
    
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    this.mediaQueryLists.forEach(mediaQuery => {
      mediaQuery.removeEventListener('change', () => {});
    });
    
    this.mediaQueryLists.clear();
  }
}